import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  children?: ReactNode
  footer?: ReactNode
  className?: string
  'data-upbo-id'?: string
  'data-upbo-label'?: string
}

/** Hộp thoại dùng chung (xác nhận xoá, bộ lọc trên mobile…). */
export function Modal({ open, onClose, title, description, children, footer, className, ...props }: ModalProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-overlay animate-fade-in" onClick={onClose} />
      <div
        data-upbo-type="modal"
        className={cn(
          'relative w-full sm:max-w-lg bg-surface border border-border shadow-float',
          'rounded-t-squircle sm:rounded-squircle max-h-[90vh] flex flex-col animate-slide-up sm:animate-scale-in',
          className,
        )}
        {...props}
      >
        <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-4 border-b border-border">
          <div className="flex flex-col gap-1">
            {title && <h2 className="text-lg font-bold">{title}</h2>}
            {description && <p className="text-sm text-text-muted">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            className="shrink-0 h-9 w-9 grid place-items-center rounded-squircle text-text-muted hover:bg-surface-2 hover:text-text transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-5 overflow-y-auto scrollbar-thin">{children}</div>
        {footer && <div className="px-5 py-4 border-t border-border bg-surface-2 rounded-b-squircle">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
